import React, { useState, useEffect } from "react";
import { DataTable, Badge, Pagination } from "@/components/ui";
import { fmt } from "@/lib/formatters";
import { Eye, Edit, ShoppingBag, Calendar, User, CreditCard, Package, RotateCcw, Truck } from "lucide-react";
import { getCustomerDisplayId } from "@/constants/globalConstants";
import { downloadFile, BASE_URL } from "@/lib/API";
import toast from "react-hot-toast";

const resolveProjectId = (sale, projects = []) => {
  const req = sale.originalRequest || sale;
  // Step 1: Find matching project from projectsLocal
  const matchedProj = Array.isArray(projects) ? projects.find(p =>
    (p._id?.toString() === req.projectId?.toString()) ||
    (p.id?.toString() === req.projectId?.toString()) ||
    (p.projectId === req.projectId) ||
    (p._id?.toString() === req.project?._id?.toString()) ||
    (p.projectId === req.project?.projectId)
  ) : null;
  // Step 2: fallback to projectDetails or project
  const proj = matchedProj || req.projectDetails || req.project;
  // Step 3: use getCustomerDisplayId
  const id = proj?.customerCustomId || req.customerCustomId || proj?.customId || getCustomerDisplayId(proj) || req.projectCustomId || req.projectId || "";
  // Skip raw 24-char MongoDB hex IDs
  if (/^[a-f0-9]{24}$/i.test(String(id))) return "";
  return id;
};

export const SalesTable = ({ sales, projects = [], onView, onEdit, onRecordReturn, onDispatch }) => {
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setPage(1);
  }, [sales]);

  const columns = [
    {
      header: "Sale Date",
      render: (s) => (
        <div className="flex items-center gap-1.5 text-slate-500">
          <Calendar size={13} className="text-slate-400" />
          <span className="text-[12px] font-normal whitespace-nowrap">
            {s.date ? new Date(s.date).toLocaleDateString() : '—'}
          </span>
        </div>
      )
    },
    {
      header: "Sale Type",
      minWidth: '120px',
      render: (s) => {
        const st = s.saleType || (s.recordType === 'material_request' ? 'Project Sale' : 'Direct Sale');
        const isProject = st === 'Project Sale';
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium border whitespace-nowrap ${
            isProject 
              ? 'bg-blue-50 text-blue-700 border-blue-200/60' 
              : 'bg-purple-50 text-purple-700 border-purple-200/60'
          }`}>
            {st}
          </span>
        );
      }
    },
    {
      header: "Verve ID",
      minWidth: '100px',
      render: (s) => {
        const idVal = s.verveId || resolveProjectId(s, projects) || '—';
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-slate-100 text-slate-700 tracking-wider font-mono whitespace-nowrap">
            {idVal}
          </span>
        );
      }
    },
    {
      header: "Customer Details",
      minWidth: '160px',
      render: (s) => {
        const isMR = s.recordType === 'material_request';
        let customerDisplay = s.customer || '—';
        if (isMR && s.projectId && customerDisplay.startsWith(s.projectId)) {
          customerDisplay = customerDisplay.replace(s.projectId, '').replace(/^\s*-\s*/, '');
        }
        return (
          <div className="flex flex-col max-w-[200px]">
            <span className="text-[13px] font-medium text-slate-700 uppercase leading-snug whitespace-nowrap overflow-hidden text-ellipsis" title={customerDisplay}>{customerDisplay}</span>
            {s.customerPhone && (
              <span className="text-[10px] text-slate-400 font-normal tracking-wide whitespace-nowrap">{s.customerPhone}</span>
            )}
          </div>
        );
      }
    },
    {
      header: "Doc Type",
      minWidth: '100px',
      render: (s) => {
        const type = s.docType || 'Order';
        return (
          <span className={`px-2 py-0.5 rounded text-[10px] font-medium tracking-wide border whitespace-nowrap ${
            type === 'Order'
              ? 'bg-orange-50 text-orange-700 border-orange-200'
              : 'bg-indigo-50 text-indigo-700 border-indigo-200'
          }`}>
            {type}
          </span>
        );
      }
    },
    {
      header: "Items",
      minWidth: '200px',
      render: (s) => {
        const items = s.items || [];
        const names = items.map(it => {
          const itemObj = (it.item && typeof it.item === 'object') ? it.item : {};
          // Item model uses 'itemName' as the item name field
          const name = itemObj.itemName || itemObj.name || itemObj.description || it.itemName || it.name || '';
          const qty = it.qty || 0;
          return name ? `${name} (x${qty})` : `(x${qty})`;
        }).filter(Boolean);
        const display = names.length > 0 ? names.join(', ') : (s.itemSummary || '—');
        return (
          <span className="text-[12px] font-normal text-slate-600 whitespace-normal break-words leading-snug block min-w-[200px]">
            {display}
          </span>
        );
      }
    },
    {
      header: "Sold Qty",
      render: (s) => (
        <span className="text-[12px] font-medium text-slate-700">{s.totalQty || (s.items || []).reduce((sum, it) => sum + (Number(it.qty) || 0), 0)}</span>
      )
    },
    {
      header: "Amount",
      render: (s) => {
        const amt = s.taxableAmount || (s.netPayable || s.totalAmount || 0);
        return <span className="text-[12px] font-medium text-slate-700">{fmt(Number(amt))}</span>;
      }
    },
    {
      header: "Discount",
      render: (s) => (
        <span className="text-[12px] font-medium text-rose-500">
          {fmt(Number(s.totalDiscount || 0))}
        </span>
      )
    },
    {
      header: "Tax",
      render: (s) => (
        <span className="text-[12px] font-medium text-slate-500">
          {fmt(Number(s.totalTax || 0))}
        </span>
      )
    },
    {
      header: "Net Bill",
      render: (s) => (
        <span className="text-[13px] font-bold text-emerald-600">
          {fmt(Number(s.netBill || s.netPayable || s.amount || 0))}
        </span>
      )
    },
    {
      header: "Dispatch",
      className: "w-28",
      render: (s) => {
        const ds = s.dispatchStatus || 'pending_store';
        const chips = {
          pending_store: 'bg-amber-50 text-amber-700 border-amber-200',
          received_store: 'bg-blue-50 text-blue-700 border-blue-200',
          dispatch_started: 'bg-purple-50 text-purple-700 border-purple-200',
          delivered: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        };
        const labels = {
          pending_store: 'Pending Store',
          received_store: 'In Store',
          dispatch_started: 'Dispatch',
          delivered: 'Delivered',
        };
        return (
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium leading-tight border ${chips[ds] || chips.pending_store}`}>
            {labels[ds] || ds}
          </span>
        );
      }
    },
    {
      header: "Action",
      className: "text-center w-32",
      render: (s) => (
        <div className="flex items-center justify-center gap-1">
          <button
            onClick={() => onDispatch && onDispatch(s)}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded-md transition-all"
            title="Dispatch"
          >
            <Truck size={16} />
          </button>
          <button
            onClick={() => onRecordReturn && onRecordReturn(s)}
            className="w-8 h-8 flex items-center justify-center text-amber-500 hover:text-white hover:bg-amber-500 rounded-md transition-all"
            title="Record Return"
          >
            <RotateCcw size={14} />
          </button>
          <button
            onClick={() => onView(s)}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-md transition-all"
            title="Preview Invoice"
          >
            <Eye size={16} />
          </button>
        </div>
      )
    }
  ];

  const getSaleTime = (s) => {
    const src = s.originalOrder || s.originalSale || {};
    const ts = new Date(src.createdAt || s.date || s.createdAt);
    return isNaN(ts) ? 0 : ts;
  };
  const sorted = [...(sales || [])].sort((a, b) => getSaleTime(b) - getSaleTime(a));
  const currentData = sorted.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const totalPages = Math.max(Math.ceil(sorted.length / itemsPerPage), 1);

  if (sorted.length === 0) return (
    <div className="p-12 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200">
      <p className="text-slate-400 font-medium text-[13px]">No sales records found</p>
    </div>
  );

  return (
    <div className="bg-white border border-slate-100 rounded-xl overflow-hidden shadow-sm">
      <DataTable columns={columns} data={currentData} noScroll={true} />
      
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </div>
  );
};
